import { countryCodeInfo, supportedCountryCodes } from "@/lib/locales"
import { ApiError } from "@/lib/vibrato-api"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import { MinusCircle } from "iconoir-react"
import { useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { VibratoClientContext } from "./contexts"
import CreateTaskTemplateCallModal from "./CreateTaskTemplateCallModal"

export default function EditContact() {
  const navigate = useNavigate()
  const { uuid } = useParams()
  const vibratoClient = useContext(VibratoClientContext)

  const [contact, setContact] = useState<any>()
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [countryCode, setCountryCode] = useState("1")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [fields, setFields] = useState<any[]>([])
  const [tags, setTags] = useState("")
  const [mergeKey, setMergeKey] = useState("")
  const [message, setMessage] = useState({ text: "", type: "" })
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [showCreateCallModal, setShowCreateCallModal] = useState(false)
  const [validationErrors, setValidationErrors] = useState("")
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    document.title = "Vibrato - Edit Contact"
  }, [])

  const addField = () => {
    setFields([...fields, { key: "", value: "" }])
  }

  const updateField = (index: any, updatedField: any) => {
    const newFields = [...fields]
    newFields[index] = updatedField
    setFields(newFields)
  }

  const removeField = (index: any) => {
    const newFields = [...fields]
    newFields.splice(index, 1)
    setFields(newFields)
  }

  const handleSubmit = async (event: any) => {
    event.preventDefault()

    let pn: string | null = phoneNumber
    if (pn) {
      pn = phoneNumber.replace(/\D/g, "")
    } else if (pn === "") {
      pn = null
    }

    let contactTags: string[] = []
    if (tags && tags.length > 0) {
      contactTags = tags.split(",").map((tag) => tag.trim())
    }

    let newContact = {
      uuid: uuid,
      first_name: firstName,
      last_name: lastName,
      country_code: countryCode,
      phone_number: pn,
      custom_fields: fields,
      merge_key: mergeKey || null,
      tags: contactTags
    }

    console.log("Saving contact", contact)

    try {
      newContact = uuid ? await vibratoClient.updateContact(newContact) : await vibratoClient.createContact(newContact)

      console.log("Contact saved")
      setMessage({ text: "Contact saved.", type: "success" })
      setContact(newContact)
      navigate(`/contacts/${newContact.uuid}`)
      setValidationErrors("")
      setFieldErrors({}) // Clear field errors on successful save
    } catch (error: any) {
      if (error instanceof ApiError) {
        if (error.data) {
          if (Array.isArray(error.data)) {
            setMessage({
              text: `Error saving contact: ${error.data.join(", ")}`,
              type: "error"
            })

            return
          }

          const fieldErrors: { [key: string]: string } = {}
          Object.keys(error.data).forEach((key) => {
            if (key === "custom_fields" && Array.isArray(error.data[key])) {
              fieldErrors[key] = error.data[key].join(", ")
            } else if (key === "custom_fields" && typeof error.data[key] === "string") {
              fieldErrors[key] = error.data[key]
            } else if (typeof error.data[key] === "string") {
              fieldErrors[key] = error.data[key]
            } else {
              fieldErrors[key] = error.data[key].join(", ")
            }
          })

          setFieldErrors(fieldErrors)
          console.log("fieldErrors", fieldErrors)

          setMessage({
            text: "Error saving contact. Please correct the errors and try again.",
            type: "error"
          })

          console.error("Failed to save contact:", error)
        } else {
          setMessage({
            text: "Error saving contact.",
            type: "error"
          })
        }
      }
    }
  }

  useEffect(() => {
    if (uuid && vibratoClient) {
      const fetchContact = async () => {
        try {
          const contact = await vibratoClient.getContact(uuid)
          setContact(contact)
          setFirstName(contact.first_name)
          setLastName(contact.last_name)
          setPhoneNumber(contact.phone_number)
          setMergeKey(contact.merge_key)

          if (contact.current_campaign_calls) {
            let displayErrs = ""
            contact.current_campaign_calls?.forEach((campaignCall: any) => {
              if (campaignCall.validation_error) {
                displayErrs += `${campaignCall.validation_error}\n`
              }
            })

            setValidationErrors(displayErrs)
          }

          if (contact.country_code) {
            setCountryCode(contact.country_code)
          }
          if (contact.custom_fields) {
            setFields(contact.custom_fields)
          }
          if (contact.tags) {
            setTags(contact.tags.join(", "))
          }
        } catch (error) {
          console.error("Failed to fetch contact:", error)
        }
      }

      fetchContact()
    }
  }, [])

  const deleteContact = async () => {
    if (!uuid) {
      return
    }

    try {
      await vibratoClient.deleteContact(uuid)
    } catch (error) {
      console.error("Failed to delete contact:", error)
    }
  }

  return (
    <div className="p-8 xl:max-w-[900px]">
      <DeleteConfirmationModal
        show={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={async () => {
          await deleteContact()
        }}
      />
      <CreateTaskTemplateCallModal
        show={showCreateCallModal}
        onClose={() => setShowCreateCallModal(false)}
        contact={contact}
      />
      <form onSubmit={handleSubmit} className="space-y-4">
        {!uuid && <h1 className="text-2xl font-bold mb-6">Create Contact</h1>}
        {uuid && <h1 className="text-2xl font-bold mb-6">Edit Contact</h1>}

        {validationErrors && (
          <div
            className="p-4 mb-4 text-sm text-yellow-800 rounded-lg bg-yellow-50 dark:bg-gray-800 dark:text-yellow-300"
            role="alert"
          >
            {validationErrors.split("\n").map((error, index) => (
              <>
                {index === 0 ? (
                  <div key={index}>
                    <span className="font-medium">Warning:</span> {error}
                  </div>
                ) : (
                  <div key={index}>{error}</div>
                )}
              </>
            ))}
          </div>
        )}

        <div className="font-thin">
          <p>
            Contacts are people, business, or organizations you can call. Use them to define who you want to call and
            how to reach them. You can add fields to contacts to store additional information about your contacts, such
            as email addresses or physical addresses.
          </p>
        </div>
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
            First Name
          </label>
          {fieldErrors.first_name && <p className="text-red-500 text-base italic mt-2">{fieldErrors.first_name}</p>}
          <input
            type="text"
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
            required
          />
        </div>
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
            Last Name <span className="text-gray-500">(optional)</span>
          </label>
          {fieldErrors.last_name && <p className="text-red-500 text-base italic mt-2">{fieldErrors.last_name}</p>}
          <input
            type="text"
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
          />
        </div>
        <div>
          <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
            Phone Number <span className="text-gray-500">(optional)</span>
          </label>
          {fieldErrors.phone_number && <p className="text-red-500 text-base italic mt-2">{fieldErrors.phone_number}</p>}
          <input
            type="tel"
            id="phoneNumber"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
          />
        </div>
        <div>
          <label htmlFor="countryCode" className="block text-sm font-medium text-gray-700">
            Country Code
          </label>
          {fieldErrors.country_code && <p className="text-red-500 text-base italic mt-2">{fieldErrors.country_code}</p>}
          <select
            id="countryCode"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            className="py-3 px-4 block w-full border-gray-200 rounded-md text-base focus:border-blue-500 focus:ring-blue-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-400"
            // defaultValue={countryCode || "1"}
          >
            {supportedCountryCodes.map((code: string) => (
              <option key={countryCodeInfo[code].code} value={countryCodeInfo[code].code}>
                {countryCodeInfo[code].displayName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="mergeKey" className="block text-sm font-medium text-gray-700">
            Merge Key <span className="text-gray-500">(optional)</span>
          </label>
          {fieldErrors.merge_key && <p className="text-red-500 text-base italic mt-2">{fieldErrors.merge_key}</p>}
          <p className="text-sm text-gray-500">
            A merge key is a unique identifier for this contact. You can use it to match with this contact when
            importing new contacts.
          </p>
          <input
            type="text"
            id="mergeKey"
            value={mergeKey}
            onChange={(e) => setMergeKey(e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
          />
        </div>
        <div>
          <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
            Tags <span className="text-gray-500">(optional)</span>
          </label>
          {fieldErrors.tags && <p className="text-red-500 text-base italic mt-2">{fieldErrors.tags}</p>}
          <p className="text-sm text-gray-500">
            Tags are labels you can use to categorize your contacts. You can use them to filter and search for contacts.
            Tags should be alphanumeric (dashes and colons are allowed) and separated by commas. For example:{" "}
            <code>customer-type:vip, production</code>.
          </p>
          <input
            type="text"
            id="tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
          />
        </div>
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-bold text-gray-700 mb-2">Custom Fields</h2>
          <div className="font-thin">
            <p>
              Custom fields allow you to store additional information about your contacts. For example, you can add a
              field for email addresses, physical addresses, or any other information you want to store. When making a
              phone call to this contact, you can use these fields to customize the call experience.
            </p>
            {fieldErrors?.custom_fields && typeof fieldErrors?.custom_fields === "string" && (
              <p className="text-red-500 text-base italic mt-2">{fieldErrors.custom_fields}</p>
            )}
          </div>
          {fields.map((field, index) => (
            <div key={index} className="flex mt-2 gap-2">
              <input
                type="text"
                placeholder="Field Name"
                value={field.key}
                onChange={(e) => updateField(index, { ...field, key: e.target.value })}
                className="grow mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
                required
              />
              {fieldErrors[`custom_fields[${index}].key`] && (
                <p className="text-red-500 text-xs italic mt-2">{fieldErrors[`custom_fields[${index}].key`]}</p>
              )}
              <input
                type="text"
                placeholder="Value"
                value={field.value}
                onChange={(e) => updateField(index, { ...field, value: e.target.value })}
                className="grow mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
                required
              />
              {fieldErrors[`custom_fields[${index}].value`] && (
                <p className="text-red-500 text-xs italic mt-2">{fieldErrors[`custom_fields[${index}].value`]}</p>
              )}
              <button type="button" onClick={() => removeField(index)} className="text-red-500">
                <MinusCircle />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addField}
            className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-fit"
          >
            Add Field
          </button>
        </div>
        <hr className="h-px my-8 bg-gray-200 border-0 dark:bg-gray-700" />
        <div className="flex gap-2">
          <button type="submit" className="bg-green-400 hover:bg-green-700 text-white font-bold py-2 px-4 rounded">
            Save Contact
          </button>
          {uuid && (
            <div className="flex grow gap-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirmation(true)}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setShowCreateCallModal(true)}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded "
              >
                Call Using Template
              </button>
            </div>
          )}
        </div>
        {message.text && (
          <div
            className={`mt-3 text-sm font-medium rounded-md ${
              message.type === "success" ? "text-green-500" : "text-red-700"
            }`}
          >
            {message.text}
          </div>
        )}
      </form>
    </div>
  )
}

function DeleteConfirmationModal({
  show,
  onClose,
  onConfirm
}: {
  show: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  const navigate = useNavigate()
  const [message, setMessage] = useState("")

  const onDelete = async () => {
    try {
      await onConfirm()
      setMessage("Contact deleted.")
      await new Promise((resolve) => setTimeout(resolve, 1000))
      navigate("/contacts")
    } catch (error) {
      console.error("Failed to delete contact:", error)
      setMessage("Error deleting contact.")
    }
  }

  return (
    <Modal show={show} onClose={onClose} className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <span className="text-purple-600">Delete Contact</span>
      </ModalHeader>
      <ModalBody>
        <p>Are you sure you want to delete this contact?</p>
      </ModalBody>
      <ModalFooter>
        {!message && (
          <>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-fit"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="mt-2 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded w-fit"
            >
              Delete
            </button>
          </>
        )}
        {message && <div className="text-green-500">{message}</div>}
      </ModalFooter>
    </Modal>
  )
}
